import React, { useEffect, useState } from 'react';
import { aiService } from '../../services';

const SUGGESTION_COLORS = {
  next_step: 'bg-blue-50 border-l-4 border-blue-400',
  improvement: 'bg-amber-50 border-l-4 border-amber-400',
  encouragement: 'bg-green-50 border-l-4 border-green-400',
  reflection: 'bg-purple-50 border-l-4 border-purple-400',
};

const SUGGESTION_LABELS = {
  next_step: 'Next step',
  improvement: 'Improvement',
  encouragement: 'Encouragement',
  reflection: 'Reflection',
};

/**
 * Phase 2 Step 8 — AI Coach.
 * Checks /api/ai/status on mount. If not configured, renders nothing silently.
 * Otherwise shows a "Get Coaching" button + scenario chat.
 */
const AICoach = ({ sessionId, participantId }) => {
  const [configured, setConfigured] = useState(null); // null = checking
  const [coaching, setCoaching] = useState(null);
  const [loadingCoach, setLoadingCoach] = useState(false);
  const [coachErr, setCoachErr] = useState('');

  const [scenarioInput, setScenarioInput] = useState('');
  const [scenarioResult, setScenarioResult] = useState(null);
  const [loadingScenario, setLoadingScenario] = useState(false);

  useEffect(() => {
    let cancelled = false;
    aiService.status()
      .then(resp => { if (!cancelled) setConfigured(!!resp?.data?.configured); })
      .catch(() => { if (!cancelled) setConfigured(false); });
    return () => { cancelled = true; };
  }, []);

  const fetchCoaching = async () => {
    setLoadingCoach(true);
    setCoachErr('');
    try {
      const resp = await aiService.getCoaching(sessionId, participantId);
      if (resp.success) setCoaching(resp.data);
      else setCoachErr(resp.error || 'Could not generate coaching');
    } catch (err) {
      setCoachErr(err.message || 'AI request failed');
    } finally {
      setLoadingCoach(false);
    }
  };

  const askScenario = async () => {
    const text = scenarioInput.trim();
    if (!text) return;
    setLoadingScenario(true);
    try {
      const resp = await aiService.getScenarioCoaching(sessionId, participantId, text);
      if (resp.success) setScenarioResult(resp.data);
      else alert(resp.error || 'Could not generate guidance');
    } catch (err) {
      alert(err.message || 'AI request failed');
    } finally {
      setLoadingScenario(false);
    }
  };

  // When the AI status check hasn't resolved yet, stay invisible (no flash-of-unavailable).
  if (configured === null) return null;

  // When AI isn't configured, render a graceful card instead of null — the parent tab
  // already shows an "AI Coach" heading, so returning null leaves users staring at a blank pane.
  if (configured === false) {
    return (
      <div className="bg-neutral-50 rounded-xl border border-neutral-200 p-6 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xl" role="img" aria-label="AI">🤖</span>
          <h2 className="text-base font-semibold text-neutral-900">Aktivity Intelligence™ (AI) Coach</h2>
        </div>
        <p className="text-sm text-neutral-600">
          Aktivity Intelligence™ (AI) is not available right now. Your progress is still being saved — a coach or
          trainer can review it with you directly, or come back later if the feature comes online.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-200 p-6 space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-2xl" role="img" aria-label="AI">🤖</span>
        <h2 className="text-base font-semibold text-neutral-900">Aktivity Intelligence™ (AI) Coach</h2>
      </div>

      {!coaching && !loadingCoach && (
        <>
          <p className="text-sm text-neutral-700">
            Based on your action plans and progress, AI can offer specific next steps and personalized suggestions.
          </p>
          <button
            onClick={fetchCoaching}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
          >
            Get Coaching Suggestions
          </button>
        </>
      )}

      {loadingCoach && <p className="text-sm text-neutral-600">Generating your coaching...</p>}
      {coachErr && <p className="text-sm text-red-600">{coachErr}</p>}

      {coaching && (
        <div className="space-y-3">
          {coaching.greeting && <p className="text-sm font-medium text-neutral-800">{coaching.greeting}</p>}
          {coaching.progressSummary && <p className="text-sm text-neutral-700">{coaching.progressSummary}</p>}

          {(coaching.suggestions || []).length > 0 && (
            <ul className="space-y-2">
              {coaching.suggestions.map((s, idx) => (
                <li key={idx} className={`p-3 rounded ${SUGGESTION_COLORS[s.type] || 'bg-neutral-50'}`}>
                  <p className="text-xs uppercase font-semibold text-neutral-500 mb-1">
                    {SUGGESTION_LABELS[s.type] || s.type}
                  </p>
                  <p className="text-sm text-neutral-800">{s.text}</p>
                </li>
              ))}
            </ul>
          )}

          {coaching.focusArea && (
            <p className="text-sm text-neutral-700">
              <span className="font-medium">Focus area:</span> {coaching.focusArea}
            </p>
          )}
          {coaching.motivationalNote && (
            <p className="text-sm italic text-neutral-600 border-l-2 border-indigo-200 pl-3">
              {coaching.motivationalNote}
            </p>
          )}

          <button
            onClick={fetchCoaching}
            className="text-xs text-indigo-600 hover:underline"
          >
            Refresh suggestions
          </button>
        </div>
      )}

      {/* Scenario chat */}
      <div className="border-t border-indigo-200 pt-4 space-y-2">
        <label className="text-sm font-medium text-neutral-800">Ask about a specific situation</label>
        <textarea
          value={scenarioInput}
          onChange={(e) => setScenarioInput(e.target.value.slice(0, 1000))}
          rows={2}
          maxLength={1000}
          placeholder="Describe a real workplace scenario you're facing..."
          className="w-full px-3 py-2 border border-indigo-200 rounded text-sm"
        />
        <div className="flex items-center justify-between">
          <button
            onClick={askScenario}
            disabled={loadingScenario || !scenarioInput.trim()}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded disabled:opacity-50"
          >
            {loadingScenario ? 'Thinking...' : 'Ask AI Coach'}
          </button>
          <span className={`text-xs ${scenarioInput.length >= 900 ? 'text-amber-600' : 'text-neutral-400'}`}>
            {scenarioInput.length}/1000
          </span>
        </div>

        {scenarioResult && (
          <div className="bg-white border border-indigo-200 rounded p-3 mt-2 space-y-2">
            {scenarioResult.approach && <p className="text-sm font-medium">{scenarioResult.approach}</p>}
            {(scenarioResult.steps || []).length > 0 && (
              <ol className="list-decimal pl-5 space-y-1 text-sm text-neutral-700">
                {scenarioResult.steps.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
            )}
            {scenarioResult.watchOutFor && (
              <p className="text-xs text-amber-700">
                <span className="font-medium">Watch out for:</span> {scenarioResult.watchOutFor}
              </p>
            )}
            {scenarioResult.followUp && (
              <p className="text-xs text-neutral-600">
                <span className="font-medium">Follow up:</span> {scenarioResult.followUp}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AICoach;
